/**
 * PI Anchor Selection Modal
 * 
 * Requires users to identify the CORRECT personal information for each
 * anchor category (Address, Name, DOB) before they can dispute other PI items.
 * 
 * ENFORCEMENT RULES:
 * - Cannot dispute ALL items in an anchor category without selecting the correct one
 * - User must confirm which record is accurate to preserve as identity anchor
 * - If correct record is NOT listed, user can manually enter it
 * - Supporting document confirmation is captured but not required
 * 
 * MANUAL ENTRY SUPPORT (April 2026):
 * - User can select "My correct [X] is not listed" for any anchor category
 * - Manual entry fields appear for address, name, or DOB
 * - Manual anchor is persisted with anchor_source: "manual"
 * 
 * SAVED ADDRESS FLOW (April 2026):
 * - When user selects manual entry for address, show saved addresses first
 * - User can choose a saved address to populate fields
 * - User can edit selected address or type new one
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Check, AlertTriangle, FileText, ChevronRight, MapPin, User, Calendar, PenLine, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import GoogleAddressInput from '../GoogleAddressInput';

// Anchor categories that require protection
const ANCHOR_CATEGORIES = ['address', 'name', 'dob'];

// Map field types to categories
const FIELD_TO_CATEGORY = {
  'current_address': 'address',
  'previous_address': 'address',
  'address': 'address',
  'name': 'name',
  'legal_name': 'name',
  'also_known_as': 'name',
  'former': 'name',
  'date_of_birth': 'dob',
  'dob': 'dob',
};

// Category display info
const CATEGORY_INFO = {
  address: {
    label: 'Address',
    icon: MapPin,
    description: 'Select your current correct address to preserve',
    manualLabel: 'My correct address is not listed',
    manualPlaceholder: 'Enter your correct current address',
    manualFields: [
      { key: 'street', label: 'Street Address', placeholder: '123 Main St Apt 4B' },
      { key: 'city', label: 'City', placeholder: 'Brooklyn' },
      { key: 'state', label: 'State', placeholder: 'NY' },
      { key: 'zip', label: 'ZIP Code', placeholder: '11201' },
    ],
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-200 dark:border-blue-700/50',
  },
  name: {
    label: 'Legal Name',
    icon: User,
    description: 'Select your correct legal name to preserve',
    manualLabel: 'My correct legal name is not listed',
    manualPlaceholder: 'Enter your correct legal name',
    manualFields: [
      { key: 'first_name', label: 'First Name', placeholder: 'John' },
      { key: 'middle_name', label: 'Middle Name (optional)', placeholder: 'Robert', optional: true },
      { key: 'last_name', label: 'Last Name', placeholder: 'Smith' },
      { key: 'suffix', label: 'Suffix (optional)', placeholder: 'Jr., Sr., III', optional: true },
    ],
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
    borderColor: 'border-purple-200 dark:border-purple-700/50',
  },
  dob: {
    label: 'Date of Birth',
    icon: Calendar,
    description: 'Select your correct date of birth to preserve',
    manualLabel: 'My correct date of birth is not listed',
    manualPlaceholder: 'Enter your correct date of birth',
    manualFields: [
      { key: 'month', label: 'Month', placeholder: 'MM', maxLength: 2 },
      { key: 'day', label: 'Day', placeholder: 'DD', maxLength: 2 },
      { key: 'year', label: 'Year', placeholder: 'YYYY', maxLength: 4 },
    ],
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
    borderColor: 'border-emerald-200 dark:border-emerald-700/50',
  },
};

// Special ID for manual entry option
const MANUAL_ENTRY_ID = '__manual_entry__';

/**
 * Improve field labels to be more descriptive
 * "Former" alone is confusing - should be "Former Name / Alias"
 */
const improveFieldLabel = (label, category) => {
  if (!label) return '';
  
  const labelLower = label.toLowerCase().trim();
  
  // Name-related improvements
  if (category === 'name') {
    if (labelLower === 'former' || labelLower === 'former name') {
      return 'Former Name / Alias';
    }
    if (labelLower === 'also known as' || labelLower === 'aka') {
      return 'Also Known As (Alias)';
    }
    if (labelLower === 'name' || labelLower === 'legal name') {
      return 'Legal Name';
    }
  }
  
  // Address-related improvements  
  if (category === 'address') {
    if (labelLower === 'previous address' || labelLower === 'former address') {
      return 'Previous Address';
    }
    if (labelLower === 'current address' || labelLower === 'address') {
      return 'Current Address';
    }
  }
  
  // Return original if no improvement needed
  return label;
};

/**
 * Categorize a PI item into address/name/dob/other
 */
const categorizeItem = (item) => {
  const field = (item.field || item.fieldLabel || '').toLowerCase();
  const fieldType = (item.field_type || item.type || '').toLowerCase();
  
  // Check direct mapping
  if (FIELD_TO_CATEGORY[field]) {
    return FIELD_TO_CATEGORY[field];
  }
  if (FIELD_TO_CATEGORY[fieldType]) {
    return FIELD_TO_CATEGORY[fieldType];
  }
  
  // Check keywords
  if (field.includes('address') || field.includes('addr')) {
    return 'address';
  }
  if (field.includes('name') || field.includes('first') || field.includes('last')) {
    return 'name';
  }
  if (field.includes('birth') || field.includes('dob')) {
    return 'dob';
  }
  
  return 'other';
};

/**
 * Format manual entry values into a single display string
 */
const formatManualValue = (category, values) => {
  if (!values) return '';
  
  switch (category) {
    case 'address':
      const parts = [values.street, values.city, values.state, values.zip].filter(Boolean);
      return parts.join(', ');
    case 'name':
      const nameParts = [values.first_name, values.middle_name, values.last_name, values.suffix].filter(Boolean);
      return nameParts.join(' ');
    case 'dob':
      if (values.month && values.day && values.year) {
        return `${values.month}/${values.day}/${values.year}`;
      }
      return '';
    default:
      return Object.values(values).filter(Boolean).join(' ');
  }
};

/**
 * Validate manual entry values
 */
const validateManualEntry = (category, values) => {
  if (!values) return false;
  
  const info = CATEGORY_INFO[category];
  if (!info?.manualFields) return false;
  
  // Check all required fields have values
  return info.manualFields.every(field => {
    if (field.optional) return true;
    return values[field.key]?.trim();
  });
};

/**
 * Extract reported date from a PI item (if address type)
 * The backend now provides reportedDates in the item
 */
const getReportedDateForItem = (item) => {
  // Check if item has reportedDates metadata (per bureau)
  if (item.reportedDates) {
    // Return the first non-null reported date found
    return item.reportedDates.TransUnion || 
           item.reportedDates.Experian || 
           item.reportedDates.Equifax || null;
  }
  return null;
};

/**
 * Normalize an address for comparison (deduplication)
 */
const normalizeAddressForComparison = (addr) => {
  const street = (addr.street_address || addr.street_line_1 || addr.address_line1 || addr.street || addr.standardized_address || '').toLowerCase().trim();
  const unit = (addr.unit || addr.street_line_2 || addr.address_line2 || addr.apt || '').toLowerCase().trim();
  const city = (addr.city || '').toLowerCase().trim();
  const state = (addr.state || '').toLowerCase().trim();
  const zip = (addr.zip || addr.postal_code || '').replace(/[^0-9]/g, '').slice(0, 5);
  
  // Remove common variations for better matching
  const normalizedStreet = street
    .replace(/\bstreet\b/g, 'st')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/\bdrive\b/g, 'dr')
    .replace(/\blane\b/g, 'ln')
    .replace(/\bcourt\b/g, 'ct')
    .replace(/\bparkway\b/g, 'pkwy')
    .replace(/\bapartment\b/g, 'apt')
    .replace(/\bsuite\b/g, 'ste')
    .replace(/\bunit\b/g, 'unit')
    .replace(/[.,#]/g, '')
    .replace(/\s+/g, ' ');
  
  return `${normalizedStreet}|${unit}|${city}|${state}|${zip}`;
};

/**
 * Deduplicate saved addresses
 */
const deduplicateSavedAddresses = (addresses) => {
  if (!addresses || addresses.length === 0) return [];
  
  const seen = new Map();
  const result = [];
  
  for (const addr of addresses) {
    const key = normalizeAddressForComparison(addr);
    if (!seen.has(key)) {
      seen.set(key, true);
      result.push(addr);
    }
  }
  
  return result;
};

/**
 * Format a saved address for display
 */
const formatSavedAddressForDisplay = (addr) => {
  const street = addr.street_address || addr.street_line_1 || addr.address_line1 || addr.street || addr.standardized_address || '';
  const unit = addr.unit || addr.street_line_2 || addr.address_line2 || addr.apt || '';
  const city = addr.city || '';
  const state = addr.state || '';
  const zip = addr.zip || addr.postal_code || '';
  
  let line1 = street.toUpperCase();
  if (unit) line1 += ` ${unit.toUpperCase()}`;
  
  const line2Parts = [];
  if (city) line2Parts.push(city.toUpperCase());
  if (state) line2Parts.push(state.toUpperCase());
  if (zip) line2Parts.push(zip);
  const line2 = line2Parts.join(', ').replace(/, ([A-Z]{2}),/, ', $1');
  
  return { line1, line2, full: `${line1}, ${line2}`.replace(/,\s*$/, '') };
};

/**
 * Get source label for saved address
 */
const getSavedAddressSourceLabel = (addr) => {
  const source = addr.source || addr.address_source || '';
  
  if (source === 'client_profile' || source === 'profile') return 'Client Profile';
  if (source === 'saved_address' || source === 'correct_address') return 'Saved Address';
  if (source === 'imported_report' || source === 'credit_report') return 'Imported Report';
  if (source === 'previously_confirmed' || source === 'confirmed') return 'Previously Confirmed';
  if (source === 'consumer_profile') return 'Consumer Profile';
  if (source === 'manual') return 'Previously Entered';
  
  return 'Saved Address';
};

/**
 * Main PI Anchor Selection Modal Component
 */
const PIAnchorSelectionModal = ({
  open,
  onClose,
  selectedPIItems,
  allPIItems,
  onConfirm,
  existingConfirmations = [],
  savedAddresses = [],  // Saved addresses from profile, reports, etc.
  clientProfileAddress = null,  // Primary client profile address
  correctAddress = null,  // Previously confirmed correct address
}) => {
  // Track anchor selections per category (can be item ID or MANUAL_ENTRY_ID)
  const [anchorSelections, setAnchorSelections] = useState({});
  // Track manual entry values per category
  const [manualEntryValues, setManualEntryValues] = useState({});
  const [supportingDocsConfirmed, setSupportingDocsConfirmed] = useState({});
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  // Track if user selected a saved address
  const [selectedSavedAddressIndex, setSelectedSavedAddressIndex] = useState(null);
  // Track saved address source for payload
  const [savedAddressSource, setSavedAddressSource] = useState(null);
  
  // Group selected PI items by category
  const selectedByCategory = useMemo(() => {
    const grouped = {};
    selectedPIItems.forEach(item => {
      const category = categorizeItem(item);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });
    return grouped;
  }, [selectedPIItems]);
  
  // Get all available PI items by category (for anchor selection options)
  const allByCategory = useMemo(() => {
    const grouped = {};
    allPIItems.forEach(item => {
      const category = categorizeItem(item);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });
    return grouped;
  }, [allPIItems]);
  
  // Determine which anchor categories need confirmation
  const categoriesNeedingConfirmation = useMemo(() => {
    const needed = [];
    ANCHOR_CATEGORIES.forEach(category => {
      // If user is disputing items in this anchor category
      if (selectedByCategory[category]?.length > 0) {
        needed.push(category);
      }
    });
    return needed;
  }, [selectedByCategory]);
  
  // Build deduplicated saved addresses from all sources
  const dedupedSavedAddresses = useMemo(() => {
    const allAddresses = [];
    
    // Priority 1: Client profile address
    if (clientProfileAddress) {
      allAddresses.push({
        ...clientProfileAddress,
        source: 'client_profile',
        _priority: 1,
      });
    }
    
    // Priority 2: Previously confirmed correct address
    if (correctAddress) {
      allAddresses.push({
        ...correctAddress,
        source: 'previously_confirmed',
        _priority: 2,
      });
    }
    
    // Priority 3: Saved addresses from props
    if (savedAddresses && savedAddresses.length > 0) {
      savedAddresses.forEach((addr, idx) => {
        allAddresses.push({
          ...addr,
          source: addr.source || 'saved_address',
          _priority: 3 + idx,
        });
      });
    }
    
    // Deduplicate
    const deduped = deduplicateSavedAddresses(allAddresses);
    
    // Sort by priority
    return deduped.sort((a, b) => (a._priority || 99) - (b._priority || 99));
  }, [clientProfileAddress, correctAddress, savedAddresses]);
  
  const hasSavedAddresses = dedupedSavedAddresses.length > 0;
  
  // Initialize from existing confirmations
  useEffect(() => {
    if (existingConfirmations && existingConfirmations.length > 0) {
      const selections = {};
      const docs = {};
      const manualValues = {};
      
      existingConfirmations.forEach(conf => {
        if (conf.anchor_source === 'manual') {
          selections[conf.category] = MANUAL_ENTRY_ID;
          manualValues[conf.category] = conf.manual_entry_values || {};
        } else {
          selections[conf.category] = conf.correct_item_id;
        }
        docs[conf.category] = conf.has_supporting_docs || false;
      });
      
      setAnchorSelections(selections);
      setManualEntryValues(manualValues);
      setSupportingDocsConfirmed(docs);
    }
  }, [existingConfirmations]);
  
  // Current category being configured
  const currentCategory = categoriesNeedingConfirmation[currentCategoryIndex];
  const categoryInfo = currentCategory ? CATEGORY_INFO[currentCategory] : null;
  
  // Items available for anchor selection in current category
  const anchorOptions = currentCategory ? (allByCategory[currentCategory] || []) : [];
  
  // Check if current category has valid selection
  const hasValidSelection = useMemo(() => {
    if (!currentCategory) return false;
    
    const selection = anchorSelections[currentCategory];
    if (!selection) return false;
    
    // If manual entry, validate the fields
    if (selection === MANUAL_ENTRY_ID) {
      return validateManualEntry(currentCategory, manualEntryValues[currentCategory]);
    }
    
    // If listed item, just need selection
    return true;
  }, [currentCategory, anchorSelections, manualEntryValues]);
  
  // Check if all categories are complete
  const allCategoriesComplete = useMemo(() => {
    return categoriesNeedingConfirmation.every(cat => {
      const selection = anchorSelections[cat];
      if (!selection) return false;
      
      if (selection === MANUAL_ENTRY_ID) {
        return validateManualEntry(cat, manualEntryValues[cat]);
      }
      
      return true;
    });
  }, [categoriesNeedingConfirmation, anchorSelections, manualEntryValues]);
  
  // Handle anchor selection (listed item or manual entry)
  const handleSelectAnchor = (itemId) => {
    setAnchorSelections(prev => ({
      ...prev,
      [currentCategory]: itemId
    }));
  };
  
  // Handle manual entry field change
  const handleManualFieldChange = (fieldKey, value) => {
    setManualEntryValues(prev => ({
      ...prev,
      [currentCategory]: {
        ...(prev[currentCategory] || {}),
        [fieldKey]: value
      }
    }));
  };
  
  // Handle selecting a saved address
  const handleSelectSavedAddress = (addr, index) => {
    const street = addr.street_address || addr.street_line_1 || addr.address_line1 || addr.street || addr.standardized_address || '';
    const unit = addr.unit || addr.street_line_2 || addr.address_line2 || addr.apt || '';
    const city = addr.city || '';
    const state = addr.state || '';
    const zip = addr.zip || addr.postal_code || '';
    
    // Populate manual entry values with saved address
    setManualEntryValues(prev => ({
      ...prev,
      [currentCategory]: {
        street: street,
        unit: unit,
        city: city,
        state: state,
        zip: zip,
        _source: 'saved_address',
        _saved_address_id: addr.id || addr._id || `saved_${index}`,
      }
    }));
    
    setSelectedSavedAddressIndex(index);
    setSavedAddressSource(addr.source || 'saved_address');
  };
  
  // Handle supporting docs toggle
  const handleToggleSupportingDocs = (category) => {
    setSupportingDocsConfirmed(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Move to next category
  const handleNext = () => {
    if (currentCategoryIndex < categoriesNeedingConfirmation.length - 1) {
      setCurrentCategoryIndex(prev => prev + 1);
    }
  };
  
  // Move to previous category
  const handlePrevious = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(prev => prev - 1);
    }
  };
  
  // Build confirmation payload and close
  const handleConfirmAll = () => {
    const confirmations = categoriesNeedingConfirmation.map(category => {
      const selectedItemId = anchorSelections[category];
      const isManual = selectedItemId === MANUAL_ENTRY_ID;
      
      if (isManual) {
        // Manual entry anchor
        const manualValues = manualEntryValues[category] || {};
        const formattedValue = formatManualValue(category, manualValues);
        
        // Determine source based on whether a saved address was used
        let source = 'manual';
        if (category === 'address' && manualValues._source === 'saved_address') {
          source = 'saved_address';
        }
        
        return {
          category,
          anchor_source: source,
          correct_item_id: null,
          correct_value: formattedValue,
          manual_entry_values: {
            ...manualValues,
            source: source,
            saved_address_id: manualValues._saved_address_id || null,
            manually_confirmed: true,
            google_verified: false,
          },
          has_supporting_docs: supportingDocsConfirmed[category] || false,
          // Normalized address payload for backend
          normalized_address: category === 'address' ? {
            street_address: manualValues.street || '',
            unit: manualValues.unit || null,
            city: manualValues.city || '',
            state: manualValues.state || '',
            zip: manualValues.zip || '',
            full_address: formattedValue,
            source: source,
            saved_address_id: manualValues._saved_address_id || null,
            manually_confirmed: true,
            google_verified: false,
          } : null,
        };
      } else {
        // Listed item anchor
        const selectedItem = (allByCategory[category] || []).find(
          item => item.id === selectedItemId
        );
        
        return {
          category,
          anchor_source: 'listed',
          correct_item_id: selectedItemId,
          correct_value: selectedItem?.values?.TransUnion || 
                         selectedItem?.values?.Experian || 
                         selectedItem?.values?.Equifax || 
                         selectedItem?.fieldLabel || '',
          manual_entry_values: null,
          has_supporting_docs: supportingDocsConfirmed[category] || false,
        };
      }
    });
    
    console.log('[PI ANCHOR] Confirmations built:', JSON.stringify(confirmations));
    onConfirm(confirmations);
    onClose();
  };
  
  // If no anchor categories need confirmation, don't show modal
  if (categoriesNeedingConfirmation.length === 0) {
    return null;
  }
  
  // Get display value for an item
  const getItemDisplayValue = (item) => {
    const values = item.values || {};
    return values.TransUnion || values.Experian || values.Equifax || item.fieldLabel || 'Unknown';
  };
  
  // Check if manual entry is selected for current category
  const isManualEntrySelected = anchorSelections[currentCategory] === MANUAL_ENTRY_ID;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-2xl min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="pi-anchor-modal">
        <DialogHeader className="min-w-0 pr-8">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl leading-snug">
            <Shield className="text-emerald-600 shrink-0" size={22} />
            <span className="break-words">Identity Anchor Protection</span>
          </DialogTitle>
          <DialogDescription className="text-stone-600 dark:text-[#C7DDD0] text-sm break-words">
            To protect your accurate personal information, please identify which record is correct for each category.
            The correct record will be preserved while inaccurate records are disputed.
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress indicator */}
        <div className="flex flex-wrap items-center gap-2 mb-4 min-w-0">
          {categoriesNeedingConfirmation.map((cat, idx) => {
            const info = CATEGORY_INFO[cat];
            const selection = anchorSelections[cat];
            const isComplete = selection && (selection !== MANUAL_ENTRY_ID || validateManualEntry(cat, manualEntryValues[cat]));
            const isCurrent = idx === currentCategoryIndex;
            
            return (
              <React.Fragment key={cat}>
                <button
                  onClick={() => setCurrentCategoryIndex(idx)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all shrink-0 ${
                    isCurrent 
                      ? `${info.bgColor} ${info.color} ring-2 ring-offset-1 ring-${info.color.replace('text-', '')}`
                      : isComplete 
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                        : 'bg-stone-100 dark:bg-[rgba(10,38,28,0.65)] text-stone-500 dark:text-[#93AE9F]'
                  }`}
                  data-testid={`anchor-step-${cat}`}
                >
                  {isComplete ? (
                    <Check size={14} />
                  ) : (
                    <info.icon size={14} />
                  )}
                  {info.label}
                </button>
                {idx < categoriesNeedingConfirmation.length - 1 && (
                  <ChevronRight size={16} className="text-stone-300 hidden sm:block shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Current category selection */}
        {currentCategory && categoryInfo && (
          <div className={`rounded-xl border-2 ${categoryInfo.borderColor} ${categoryInfo.bgColor} p-3 sm:p-4 min-w-0 overflow-hidden`}>
            <div className="flex items-center gap-2 mb-3 min-w-0">
              <categoryInfo.icon className={`${categoryInfo.color} shrink-0`} size={20} />
              <h3 className={`font-semibold ${categoryInfo.color} break-words`}>
                Select Your Correct {categoryInfo.label}
              </h3>
            </div>
            <p className="text-sm text-stone-600 dark:text-[#C7DDD0] mb-4 break-words">
              {categoryInfo.description}. This record will be preserved on your credit file.
            </p>
            
            {/* Anchor options from listed records */}
            <div className="space-y-2" data-testid={`anchor-options-${currentCategory}`}>
              {anchorOptions.map(item => {
                const isSelected = anchorSelections[currentCategory] === item.id;
                const displayValue = getItemDisplayValue(item);
                const isBeingDisputed = selectedPIItems.some(sel => sel.id === item.id);
                const reportedDate = getReportedDateForItem(item);
                
                // Check if this item's value MATCHES the protected anchor value
                // If another item is selected as the anchor, and THIS item has the same value,
                // it should show as "Matches Protected" not "Will Be Disputed"
                const selectedAnchorId = anchorSelections[currentCategory];
                const selectedAnchorItem = selectedAnchorId && selectedAnchorId !== MANUAL_ENTRY_ID 
                  ? anchorOptions.find(opt => opt.id === selectedAnchorId)
                  : null;
                const selectedAnchorValue = selectedAnchorItem ? getItemDisplayValue(selectedAnchorItem) : null;
                
                // Normalize for comparison (case-insensitive, trim)
                const normalizeForComparison = (val) => {
                  if (!val) return '';
                  return val.toString().trim().toLowerCase().replace(/\s+/g, ' ');
                };
                
                const itemValueNormalized = normalizeForComparison(displayValue);
                const anchorValueNormalized = normalizeForComparison(selectedAnchorValue);
                
                // Check if this item matches the protected anchor (but isn't the anchor itself)
                const matchesProtectedAnchor = !isSelected && 
                  selectedAnchorValue && 
                  itemValueNormalized && 
                  anchorValueNormalized &&
                  itemValueNormalized === anchorValueNormalized;
                
                // If matches anchor OR is selected, it's protected
                const isProtectedOrPreserved = isSelected || matchesProtectedAnchor;
                
                return (
                  <div
                    key={item.id}
                    onClick={() => handleSelectAnchor(item.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all min-w-0 overflow-hidden ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 ring-1 ring-emerald-200 dark:ring-emerald-700'
                        : matchesProtectedAnchor
                          ? 'border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] hover:border-stone-300 dark:hover:border-[rgba(142,218,182,0.30)]'
                    }`}
                    data-testid={`anchor-option-${item.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        isSelected 
                          ? 'border-emerald-500 bg-emerald-500' 
                          : matchesProtectedAnchor
                            ? 'border-blue-400 bg-blue-400'
                            : 'border-stone-300 dark:border-[rgba(142,218,182,0.40)]'
                      }`}>
                        {isSelected && <Check size={12} className="text-white" />}
                        {matchesProtectedAnchor && !isSelected && <Shield size={10} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-stone-900 dark:text-[#EAF7F0] break-words">
                            {improveFieldLabel(item.fieldLabel, currentCategory)}
                          </span>
                          {/* Badge logic: Protected > Matches Protected > Will Be Disputed */}
                          {isSelected && (
                            <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700 text-xs">
                              Protected Anchor
                            </Badge>
                          )}
                          {matchesProtectedAnchor && (
                            <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 border-blue-200 dark:border-blue-700 text-xs">
                              Matches Protected - Preserved
                            </Badge>
                          )}
                          {isBeingDisputed && !isProtectedOrPreserved && (
                            <Badge variant="outline" className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 text-xs">
                              Will Be Disputed
                            </Badge>
                          )}
                          {/* Show reported date as subtle metadata badge */}
                          {reportedDate && (
                            <Badge 
                              variant="outline" 
                              className="bg-stone-50 dark:bg-[rgba(142,218,182,0.12)] text-stone-500 dark:text-[#93AE9F] border-stone-200 dark:border-[rgba(142,218,182,0.20)] text-xs font-normal"
                              data-testid={`reported-date-badge-${item.id}`}
                            >
                              Reported: {reportedDate}
                            </Badge>
                          )}
                        </div>
                        {/* Show warning if user tries to select a matching value */}
                        {matchesProtectedAnchor && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                            This value matches the protected anchor and will be preserved, not disputed.
                          </p>
                        )}
                        <p className="text-sm text-stone-600 dark:text-[#C7DDD0] break-words whitespace-normal">
                          {displayValue}
                        </p>
                        
                        {/* Bureau values — stack on mobile, 3-col on sm+ */}
                        {item.values && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 min-w-0">
                            {['TransUnion', 'Experian', 'Equifax'].map(bureau => {
                              const val = item.values[bureau];
                              const bureauDate = item.reportedDates?.[bureau];
                              return (
                                <div key={bureau} className="text-xs min-w-0 break-words rounded-md bg-stone-50/80 dark:bg-[rgba(10,38,28,0.35)] px-2 py-1.5 sm:bg-transparent sm:dark:bg-transparent sm:px-0 sm:py-0">
                                  <span className="font-medium text-stone-500 dark:text-[#93AE9F] block sm:inline">
                                    {bureau}:
                                  </span>{' '}
                                  <span className="text-stone-600 dark:text-[#C7DDD0] break-words">
                                    {val ? (typeof val === 'string' ? val : '...') : '—'}
                                  </span>
                                  {bureauDate && (
                                    <span className="text-stone-400 dark:text-[#7A9B8A] italic block sm:inline sm:ml-1">
                                      ({bureauDate})
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Manual Entry Option - ALWAYS show this */}
              <div
                onClick={() => handleSelectAnchor(MANUAL_ENTRY_ID)}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all min-w-0 overflow-hidden ${
                  isManualEntrySelected
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 ring-1 ring-purple-200 dark:ring-purple-700'
                    : 'border-dashed border-stone-300 dark:border-[rgba(142,218,182,0.25)] bg-stone-50 dark:bg-[rgba(10,38,28,0.50)] hover:border-stone-400 dark:hover:border-[rgba(142,218,182,0.35)]'
                }`}
                data-testid={`anchor-option-manual-${currentCategory}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isManualEntrySelected 
                      ? 'border-purple-500 bg-purple-500' 
                      : 'border-stone-400 dark:border-[rgba(142,218,182,0.40)]'
                  }`}>
                    {isManualEntrySelected ? (
                      <Check size={12} className="text-white" />
                    ) : (
                      <PenLine size={10} className="text-stone-400 dark:text-[#93AE9F]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-medium ${isManualEntrySelected ? 'text-purple-900 dark:text-purple-300' : 'text-stone-700 dark:text-[#C7DDD0]'}`}>
                        {categoryInfo.manualLabel}
                      </span>
                      {isManualEntrySelected && (
                        <Badge variant="outline" className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 text-xs">
                          Manual Entry
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-500 dark:text-[#93AE9F]">
                      Enter your correct information manually if it's not shown above
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Manual Entry Form - Show when manual entry is selected */}
            {isManualEntrySelected && categoryInfo.manualFields && (
              <div className="mt-4 p-4 bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-lg border border-purple-200 dark:border-purple-700/50" data-testid={`manual-entry-form-${currentCategory}`}>
                <div className="flex items-center gap-2 mb-3">
                  <PenLine size={16} className="text-purple-600 dark:text-purple-400" />
                  <span className="font-medium text-purple-900 dark:text-purple-300">Enter Your Correct {categoryInfo.label}</span>
                </div>
                
                {/* ADDRESS CATEGORY - Show saved addresses first, then manual form */}
                {currentCategory === 'address' ? (
                  <div data-testid="address-entry-section">
                    {/* SAVED ADDRESS SELECTOR */}
                    {hasSavedAddresses && (
                      <div className="mb-4" data-testid="saved-addresses-selector">
                        <div className="flex items-center gap-2 mb-3">
                          <Building2 size={16} className="text-emerald-600 dark:text-emerald-400" />
                          <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">Use an address on file</span>
                        </div>
                        
                        <div className="space-y-2">
                          {dedupedSavedAddresses.map((addr, idx) => {
                            const display = formatSavedAddressForDisplay(addr);
                            const sourceLabel = getSavedAddressSourceLabel(addr);
                            const isSelected = selectedSavedAddressIndex === idx;
                            
                            return (
                              <div
                                key={idx}
                                className={`p-3 rounded-xl border-2 cursor-pointer transition-all min-w-0 overflow-hidden ${
                                  isSelected
                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 ring-1 ring-emerald-200 dark:ring-emerald-700'
                                    : 'border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] hover:border-emerald-300 dark:hover:border-emerald-600 hover:bg-emerald-50/30 dark:hover:bg-[rgba(16,185,129,0.10)]'
                                }`}
                                onClick={() => handleSelectSavedAddress(addr, idx)}
                                data-testid={`saved-address-card-${idx}`}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 min-w-0">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-stone-900 dark:text-[#EAF7F0] break-words">{display.line1}</div>
                                    <div className="text-sm text-stone-600 dark:text-[#C7DDD0] mt-0.5 break-words">{display.line2}</div>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant="outline" className="text-xs bg-stone-100 dark:bg-[rgba(142,218,182,0.12)] text-stone-600 dark:text-[#93AE9F] border-stone-200 dark:border-[rgba(142,218,182,0.20)]">
                                        {sourceLabel}
                                      </Badge>
                                      {isSelected && (
                                        <Badge variant="outline" className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700">
                                          <Check size={10} className="mr-1" />
                                          Selected
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant={isSelected ? "default" : "outline"}
                                    className={`w-full sm:w-auto shrink-0 ${isSelected ? "bg-emerald-600 hover:bg-emerald-700" : "dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectSavedAddress(addr, idx);
                                    }}
                                    data-testid={`use-saved-address-btn-${idx}`}
                                  >
                                    {isSelected ? 'Selected' : 'Use This Address'}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Divider */}
                        <div className="relative my-5">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-stone-200 dark:border-[rgba(142,218,182,0.15)]"></div>
                          </div>
                          <div className="relative flex justify-center">
                            <span className="bg-white dark:bg-[#0B1F18] px-3 text-sm text-stone-500 dark:text-[#93AE9F]">Or enter a new correct address manually</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* MANUAL ADDRESS FORM */}
                    <div data-testid="manual-address-form">
                      <GoogleAddressInput
                        label={hasSavedAddresses ? "" : ""}
                        placeholder="Street Address"
                        sourceScreen="identity_anchor_modal"
                        showSaveButton={false}
                        savedAddresses={[]} // Don't pass to inner component since we handle it above
                        initialValue={manualEntryValues[currentCategory]?.street || ''}
                        initialStreetLine2={manualEntryValues[currentCategory]?.unit || ''}
                        initialCity={manualEntryValues[currentCategory]?.city || ''}
                        initialState={manualEntryValues[currentCategory]?.state || ''}
                        initialZip={manualEntryValues[currentCategory]?.zip || ''}
                        onAddressComplete={(normalizedAddress) => {
                          // Update manual entry values with complete address
                          handleManualFieldChange('street', normalizedAddress.street_address || '');
                          handleManualFieldChange('unit', normalizedAddress.unit || '');
                          handleManualFieldChange('city', normalizedAddress.city || '');
                          handleManualFieldChange('state', normalizedAddress.state || '');
                          handleManualFieldChange('zip', normalizedAddress.zip || '');
                          // Store full normalized result for later use
                          handleManualFieldChange('_validated_address', normalizedAddress);
                          // Mark as edited if it was from a saved address
                          if (selectedSavedAddressIndex !== null) {
                            handleManualFieldChange('_source', 'saved_address');
                          } else {
                            handleManualFieldChange('_source', 'manual');
                          }
                        }}
                        onChange={(data) => {
                          // Update fields as user types
                          if (data.street_address !== undefined) {
                            handleManualFieldChange('street', data.street_address);
                          }
                          if (data.unit !== undefined) {
                            handleManualFieldChange('unit', data.unit);
                          }
                          if (data.city !== undefined) {
                            handleManualFieldChange('city', data.city);
                          }
                          if (data.state !== undefined) {
                            handleManualFieldChange('state', data.state);
                          }
                          if (data.zip !== undefined) {
                            handleManualFieldChange('zip', data.zip);
                          }
                          // If user edits after selecting saved address, track it
                          if (selectedSavedAddressIndex !== null) {
                            handleManualFieldChange('_source', 'saved_address');
                          }
                        }}
                      />
                    </div>
                    
                    {/* Show selected/preview info */}
                    {manualEntryValues[currentCategory]?.street && 
                     manualEntryValues[currentCategory]?.city && 
                     manualEntryValues[currentCategory]?.state && 
                     manualEntryValues[currentCategory]?.zip && (
                      <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="address-preview">
                        <div className="flex items-center gap-2 text-sm font-medium text-emerald-800 mb-1">
                          <Check size={14} />
                          {selectedSavedAddressIndex !== null ? 'Address Selected' : 'Address Complete'}
                        </div>
                        <div className="text-sm text-emerald-700">
                          {formatManualValue('address', manualEntryValues[currentCategory])}
                        </div>
                        {manualEntryValues[currentCategory]?._source && (
                          <Badge 
                            variant="outline" 
                            className="mt-2 text-xs bg-emerald-100 text-emerald-700 border-emerald-300"
                          >
                            Source: {manualEntryValues[currentCategory]._source === 'saved_address' ? 'Saved Address' : 'Manual Entry'}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Standard manual entry fields for name and DOB */
                  <>
                    <div className={`grid gap-3 ${
                      currentCategory === 'dob' ? 'grid-cols-3' : 
                      currentCategory === 'name' ? 'grid-cols-1 sm:grid-cols-2' : 
                      'grid-cols-1'
                    }`}>
                      {categoryInfo.manualFields.map(field => (
                        <div key={field.key} className={field.key === 'street' ? 'col-span-full' : ''}>
                          <Label htmlFor={`manual-${currentCategory}-${field.key}`} className="text-sm text-stone-700">
                            {field.label}
                          </Label>
                          <Input
                            id={`manual-${currentCategory}-${field.key}`}
                            placeholder={field.placeholder}
                            maxLength={field.maxLength}
                            value={manualEntryValues[currentCategory]?.[field.key] || ''}
                            onChange={(e) => handleManualFieldChange(field.key, e.target.value)}
                            className="mt-1"
                            data-testid={`manual-input-${currentCategory}-${field.key}`}
                          />
                        </div>
                      ))}
                    </div>
                    
                    {/* Show formatted preview */}
                    {validateManualEntry(currentCategory, manualEntryValues[currentCategory]) && (
                      <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                        <span className="text-xs font-medium text-emerald-700">Preview:</span>
                        <p className="text-sm text-emerald-900 mt-0.5">
                          {formatManualValue(currentCategory, manualEntryValues[currentCategory])}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Supporting documents confirmation */}
            {hasValidSelection && (
              <div className="mt-4 pt-4 border-t border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                <label 
                  className="flex items-start gap-3 cursor-pointer"
                  data-testid={`supporting-docs-${currentCategory}`}
                >
                  <Checkbox
                    checked={supportingDocsConfirmed[currentCategory] || false}
                    onCheckedChange={() => handleToggleSupportingDocs(currentCategory)}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-900 dark:text-[#EAF7F0]">
                      I have supporting documentation
                    </span>
                    <p className="text-xs text-slate-600 dark:text-[#C7DDD0] mt-0.5">
                      (e.g., utility bill, government ID, lease agreement)
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}
        
        {/* Info banner */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-[rgba(30,45,22,0.75)] dark:border-amber-500/45 min-w-0">
          <AlertTriangle size={18} className="text-amber-500 dark:text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm min-w-0 break-words">
            <span className="font-bold text-amber-800 dark:text-amber-200">
              Why is this required?
            </span>{' '}
            <span className="text-amber-800 dark:text-[#EAF7F0]">
              Credit bureaus maintain your identity records.
              Disputing ALL records in a category could leave no accurate information on file,
              potentially causing issues with credit applications and identity verification.
            </span>
          </div>
        </div>
        
        {/* Navigation buttons */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
          <Button
            variant="outline"
            onClick={currentCategoryIndex > 0 ? handlePrevious : onClose}
            data-testid="anchor-modal-back"
            className="w-full sm:w-auto"
          >
            {currentCategoryIndex > 0 ? 'Previous' : 'Cancel'}
          </Button>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {currentCategoryIndex < categoriesNeedingConfirmation.length - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!hasValidSelection}
                data-testid="anchor-modal-next"
                className="flex-1 sm:flex-none"
              >
                Next
                <ChevronRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleConfirmAll}
                disabled={!allCategoriesComplete}
                className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700"
                data-testid="anchor-modal-confirm"
              >
                <Check size={16} className="mr-1" />
                Confirm Anchors
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PIAnchorSelectionModal;
export { categorizeItem, ANCHOR_CATEGORIES, CATEGORY_INFO, MANUAL_ENTRY_ID, formatManualValue };
